import { Grid, Stack } from "@mui/material";
import { StatCard } from "components/common/StatCard";
import { ExcelDownloadButton } from "components/common/ExcelDownloadButton";
import { useTenantTranslation } from "hooks/formatters";
import { useFormatNumber } from "src/utils/formatNumber";
import { useIsBehindFeatureFlag } from "hooks/useFeatureFlag";
import { useIsAdmin, useTenant } from "hooks/settings";
import { useStringQueryParamState } from "hooks/useStringQueryParamState";
import { useEffect, useState } from "react";
import { backendApi, downloadAsExcel } from "src/store/api/enhancedApi";
import { ToggleSelector } from "../ToggleSelector";
import { GroupedScrapChefUsageSeries } from "../types";
import { ScrapChefUsageDetailedView } from "./ScrapChefUsageDetailedView";
import { ScrapChefUsagePlot } from "./ScrapChefUsagePlot";
import { formatTrend } from "./usageFormatting";

type UsageView = "summary" | "detailed";

const USAGE_VIEW_LABELS: Record<UsageView, string> = {
  summary: "summary",
  detailed: "detailed",
};

interface ScrapChefUsageTabProps {
  groupedScrapChefUsageSeries: GroupedScrapChefUsageSeries;
}

export const ScrapChefUsageTab = ({
  groupedScrapChefUsageSeries,
}: ScrapChefUsageTabProps) => {
  const showDetailedToggle = useIsBehindFeatureFlag(
    "scrap_chef_usage_detailed"
  );
  const [view, setView] = useStringQueryParamState<UsageView>(
    "usageView",
    "summary"
  );

  return (
    <div>
      {showDetailedToggle ? (
        <Stack direction="row" sx={{ mt: 2 }}>
          <ToggleSelector<UsageView>
            selectedValue={view}
            setSelectedValue={setView}
            availableValueLabels={USAGE_VIEW_LABELS}
          />
        </Stack>
      ) : null}
      {view === "detailed" ? (
        <ScrapChefUsageDetailedView />
      ) : (
        <ScrapChefUsageSummary
          groupedScrapChefUsageSeries={groupedScrapChefUsageSeries}
        />
      )}
    </div>
  );
};

const ScrapChefUsageSummary = ({
  groupedScrapChefUsageSeries,
}: ScrapChefUsageTabProps) => {
  const isAdmin = useIsAdmin();
  const { t } = useTenantTranslation();
  const formatNumber = useFormatNumber();

  const [selectedDate, setSelectedDate] = useState<Date | null>(null);

  useEffect(() => {
    // eslint-disable-next-line @eslint-react/set-state-in-effect -- resets date on data change; user also clicks plot
    setSelectedDate(getDefaultSelectedDate(groupedScrapChefUsageSeries));
  }, [groupedScrapChefUsageSeries]);

  const { currentUsagePercentage, trendPercentage } = getMetricsForDate(
    groupedScrapChefUsageSeries,
    selectedDate
  );
  const trend = formatTrend(trendPercentage, formatNumber);

  return (
    <div>
      <Stack direction="row" justifyContent="flex-end" sx={{ mt: 2 }}>
        {isAdmin ? <UsageExcelButton /> : null}
      </Stack>
      <Grid container spacing={2} mt={1}>
        <Grid size={{ xs: 4 }}>
          <StatCard
            label={t("currentUsage")}
            value={`${formatNumber(1, true)(currentUsagePercentage)}%`}
            caption={t("sevenDayAverage")}
          />
        </Grid>
        <Grid size={{ xs: 4 }}>
          <StatCard
            label={t("trend")}
            value={trend.text}
            valueColor={trend.color}
            caption={t("last30Days")}
          />
        </Grid>
        <Grid size={{ xs: 4 }}>
          <StatCard
            label={t("rampUpTime")}
            value={
              groupedScrapChefUsageSeries.rampUpTime
                ? `${groupedScrapChefUsageSeries.rampUpTime} ${t("days")}`
                : "-"
            }
            caption={t("rampUpTimeDescription")}
          />
        </Grid>
      </Grid>
      <Grid container spacing={1} mt={2} mx={0}>
        <Grid size={{ xs: 12 }} ml={0}>
          <ScrapChefUsagePlot
            scrapChefUsageSeries={groupedScrapChefUsageSeries}
            setSelectedDate={setSelectedDate}
          />
        </Grid>
      </Grid>
    </div>
  );
};

const getMetricsForDate = (
  series: GroupedScrapChefUsageSeries,
  date: Date | null
) => {
  if (!date || series.values.date.length === 0) {
    return {
      currentUsagePercentage: series.currentUsagePercentage,
      trendPercentage: series.trendPercentage,
    };
  }

  const dateString = date.toISOString().split("T")[0];
  const closestIndex = series.values.date.findIndex((d) => d === dateString);
  if (closestIndex === -1) {
    return {
      currentUsagePercentage: series.currentUsagePercentage,
      trendPercentage: series.trendPercentage,
    };
  }

  return {
    currentUsagePercentage:
      series.values.currentUsagePercentages[closestIndex] ??
      series.currentUsagePercentage,
    trendPercentage:
      series.values.trendPercentages[closestIndex] ?? series.trendPercentage,
  };
};

const getDefaultSelectedDate = (
  scrapChefUsageSeries: GroupedScrapChefUsageSeries
): Date | null => {
  const defaultSelectedDateString =
    scrapChefUsageSeries.values.date.length > 0
      ? scrapChefUsageSeries.values.date[
          scrapChefUsageSeries.values.date.length - 1
        ]
      : null;
  return defaultSelectedDateString ? new Date(defaultSelectedDateString) : null;
};

const UsageExcelButton = () => {
  const tenantName = useTenant();
  const [trigger] =
    backendApi.endpoints.getStrategyDashboardScrapChefUsage.useLazyQuery();
  const getBlob = () =>
    downloadAsExcel(trigger, {
      tenantName,
    });
  return <ExcelDownloadButton variant="outlined" getBlob={getBlob} />;
};
